// ============================================
// FAMUZA MARKET — Seller Onboarding
// Super simple — built for WhatsApp users
// One question at a time, big buttons, plain language
// ============================================

import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const WaIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

// Category options with icons — visual, no jargon
const CATEGORIES = [
  { id: 'salaula', label: 'Salaula', sublabel: 'Used clothing', icon: '👗' },
  { id: 'returns-asda', label: 'Returns — ASDA/George', sublabel: 'Almost new clothes & shoes', icon: '🛍️' },
  { id: 'returns-amazon', label: 'Returns — Amazon UK', sublabel: 'Electronics & household', icon: '📦' },
  { id: 'returns-lidl', label: 'Returns — Lidl', sublabel: 'General household goods', icon: '🏠' },
  { id: 'furniture', label: 'Furniture — Wayfair UK', sublabel: 'Chairs, tables, lamps', icon: '🛋️' },
  { id: 'shoes', label: 'Shoes', sublabel: 'All footwear', icon: '👟' },
  { id: 'toys-used', label: 'Used Toys', sublabel: 'Second hand toys', icon: '🧸' },
  { id: 'toys-new', label: 'New Toys — Amazon', sublabel: 'Brand new toy stock', icon: '🎁' },
  { id: 'baby', label: 'Baby Products', sublabel: 'Cups, bottles, sterilisers etc', icon: '🍼' },
  { id: 'food', label: 'Pasta & Food', sublabel: 'UNIFAM food imports', icon: '🍝' },
  { id: 'bulk', label: 'Bulk Boxes', sublabel: 'Wholesale boxes', icon: '📦' },
  { id: 'electronics', label: 'Electronics', sublabel: 'Phones, gadgets, appliances', icon: '📱' },
];

// Condition options — plain language
const CONDITIONS = [
  { id: 'new', label: 'Brand New', sublabel: 'Never been used, still in packaging', icon: '✨' },
  { id: 'returns', label: 'Almost New', sublabel: 'UK returns — barely used', icon: '🔄' },
  { id: 'used-good', label: 'Used — Good Condition', sublabel: 'Used but works perfectly', icon: '👍' },
  { id: 'used-fair', label: 'Used — Fair Condition', sublabel: 'Some wear but still works', icon: '👌' },
];

export default function Onboarding() {
  const { showNotification } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 5;

  // Form data
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    location: '',
    isExistingCustomer: null,
    shopName: '',
    categories: [],
    itemName: '',
    category: '',
    condition: '',
    price: '',
    description: '',
    photos: [],
    agreeTerms: false,
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleCategory = (id) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(id)
        ? prev.categories.filter(c => c !== id)
        : [...prev.categories, id]
    }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map(f => URL.createObjectURL(f));
    setForm(prev => ({ ...prev, photos: [...prev.photos, ...urls].slice(0, 6) }));
  };

  const removePhoto = (index) => {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const nextStep = () => {
    window.scrollTo(0, 0);
    setStep(s => Math.min(s + 1, TOTAL_STEPS));
  };

  const prevStep = () => {
    window.scrollTo(0, 0);
    setStep(s => Math.max(s - 1, 1));
  };

  const handleSubmit = () => {
    showNotification('Application submitted! We will contact you on WhatsApp within 24 hours.', 'success');
    setStep(TOTAL_STEPS);
  };

  // Styles
  const wrap = { maxWidth: 560, margin: '0 auto', padding: '2rem 1.25rem 4rem' };
  const card = { background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '2rem' };
  const label = { fontSize: '0.78rem', color: 'rgba(245,240,232,0.55)', marginBottom: '0.4rem', display: 'block' };
  const input = { width: '100%', background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', color: '#F5F0E8', outline: 'none', boxSizing: 'border-box' };
  const bigBtn = { width: '100%', padding: '1rem', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 500, cursor: 'pointer', border: 'none', marginTop: '0.5rem', transition: 'all 0.2s' };
  const backBtn = { width: '100%', padding: '0.875rem', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.5)', marginTop: '0.6rem' };

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.2rem', color: '#F5F0E8', textDecoration: 'none' }}>
          FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
        </Link>
        <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.45)' }}>
          Already selling? <Link to="/login" style={{ color: '#E8622A' }}>Log in</Link>
        </div>
      </div>

      <div style={wrap}>

        {/* Progress bar */}
        {step < TOTAL_STEPS && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'rgba(245,240,232,0.4)', marginBottom: '0.6rem' }}>
              <span>Step {step} of {TOTAL_STEPS - 1}</span>
              <span>{Math.round((step / (TOTAL_STEPS - 1)) * 100)}% done</span>
            </div>
            <div style={{ height: 6, background: '#1E1E1E', borderRadius: 100, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#E8622A', borderRadius: 100, width: `${(step / (TOTAL_STEPS - 1)) * 100}%`, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        )}

        {/* ========== STEP 1: WHO ARE YOU ========== */}
        {step === 1 && (
          <div style={card}>
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👋</div>
              <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.4rem' }}>
                Welcome to FAMUZA!
              </h1>
              <p style={{ fontSize: '0.88rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>
                Let's set up your seller account. Takes less than 5 minutes!
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={label}>Your first name</label>
                <input style={input} type="text" placeholder="e.g. Bwalya" value={form.firstName} onChange={e => update('firstName', e.target.value)} />
              </div>
              <div>
                <label style={label}>Your last name</label>
                <input style={input} type="text" placeholder="e.g. Mwansa" value={form.lastName} onChange={e => update('lastName', e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>Your WhatsApp number</label>
              <div style={{ display: 'flex', background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '0.85rem 1rem', background: 'rgba(37,211,102,0.08)', color: '#25D366', fontSize: '0.88rem', fontWeight: 500, borderRight: '0.5px solid rgba(255,255,255,0.07)', whiteSpace: 'nowrap' }}>
                  🇿🇲 +260
                </div>
                <input style={{ ...input, border: 'none', borderRadius: 0 }} type="tel" placeholder="97 123 4567" value={form.phone} onChange={e => update('phone', e.target.value)} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)', marginTop: '0.35rem' }}>
                Buyers will contact you on this number — make sure it is active on WhatsApp
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>Where are you in Lusaka?</label>
              <select style={{ ...input, cursor: 'pointer' }} value={form.location} onChange={e => update('location', e.target.value)}>
                <option value="">Select your area</option>
                {['Lusaka Central', 'Chilenje', 'Woodlands', 'Kalingalinga', 'Matero', 'Kabulonga', 'Libala', 'Chelstone', 'Emmasdale', 'Meanwood', 'Northmead', 'Roma', 'Other'].map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ ...label, marginBottom: '0.6rem' }}>Are you one of our existing UNIFAM customers?</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                {[['yes', 'Yes — I buy from UNIFAM', '✅'], ['no', 'No — I am new here', '👋']].map(([val, text, icon]) => (
                  <div
                    key={val}
                    onClick={() => update('isExistingCustomer', val)}
                    style={{
                      padding: '0.875rem',
                      borderRadius: 12,
                      border: `0.5px solid ${form.isExistingCustomer === val ? '#E8622A' : 'rgba(255,255,255,0.07)'}`,
                      background: form.isExistingCustomer === val ? 'rgba(232,98,42,0.1)' : '#1E1E1E',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '0.82rem',
                      color: form.isExistingCustomer === val ? '#E8622A' : 'rgba(245,240,232,0.6)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>{icon}</div>
                    {text}
                  </div>
                ))}
              </div>
              {form.isExistingCustomer === 'yes' && (
                <div style={{ marginTop: '0.5rem', background: 'rgba(37,211,102,0.08)', border: '0.5px solid rgba(37,211,102,0.2)', borderRadius: 8, padding: '0.6rem 0.875rem', fontSize: '0.75rem', color: '#25D366' }}>
                  &#10003; Existing customers get priority approval — usually within a few hours!
                </div>
              )}
            </div>

            <button
              onClick={nextStep}
              disabled={!form.firstName || !form.phone || !form.location}
              style={{ ...bigBtn, background: form.firstName && form.phone && form.location ? '#E8622A' : '#2A2A2A', color: form.firstName && form.phone && form.location ? '#fff' : 'rgba(245,240,232,0.3)' }}
            >
              Continue &rarr;
            </button>
          </div>
        )}

        {/* ========== STEP 2: YOUR SHOP ========== */}
        {step === 2 && (
          <div style={card}>
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏪</div>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.4rem' }}>
                Name your shop
              </h2>
              <p style={{ fontSize: '0.88rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>
                This is the name buyers will see. Keep it simple and easy to remember.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>Shop name</label>
              <input style={input} type="text" placeholder="e.g. Bwalya's Clothing Store" value={form.shopName} onChange={e => update('shopName', e.target.value)} />
              {form.shopName && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)' }}>
                  Your shop link will be: famuzamarket.com/shop/{form.shopName.toLowerCase().replace(/\s+/g, '-')}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ ...label, marginBottom: '0.75rem' }}>What do you sell? <span style={{ color: 'rgba(245,240,232,0.35)' }}>(tick all that apply)</span></label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {CATEGORIES.map(cat => (
                  <div
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: 12,
                      border: `0.5px solid ${form.categories.includes(cat.id) ? '#E8622A' : 'rgba(255,255,255,0.07)'}`,
                      background: form.categories.includes(cat.id) ? 'rgba(232,98,42,0.1)' : '#1E1E1E',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{cat.icon}</span>
                    <div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 500, color: form.categories.includes(cat.id) ? '#E8622A' : '#F5F0E8' }}>{cat.label}</div>
                      <div style={{ fontSize: '0.65rem', color: 'rgba(245,240,232,0.4)' }}>{cat.sublabel}</div>
                    </div>
                    {form.categories.includes(cat.id) && (
                      <div style={{ marginLeft: 'auto', width: 18, height: 18, borderRadius: '50%', background: '#E8622A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#fff', flexShrink: 0 }}>&#10003;</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={nextStep} disabled={!form.shopName || form.categories.length === 0} style={{ ...bigBtn, background: form.shopName && form.categories.length > 0 ? '#E8622A' : '#2A2A2A', color: form.shopName && form.categories.length > 0 ? '#fff' : 'rgba(245,240,232,0.3)' }}>
              Continue &rarr;
            </button>
            <button onClick={prevStep} style={backBtn}>&larr; Back</button>
          </div>
        )}

        {/* ========== STEP 3: ADD YOUR FIRST ITEM ========== */}
        {step === 3 && (
          <div style={card}>
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📸</div>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.4rem' }}>
                Add your first item
              </h2>
              <p style={{ fontSize: '0.88rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>
                Don't worry about being perfect — a clear photo and honest price is all you need!
              </p>
            </div>

            {/* Photo upload — camera + gallery both prominent */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ ...label, marginBottom: '0.75rem' }}>Photos of your item</label>

              {/* Photo previews */}
              {form.photos.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {form.photos.map((photo, i) => (
                    <div key={i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 10, overflow: 'hidden' }}>
                      <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => removePhoto(i)} style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', fontSize: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&#10005;</button>
                    </div>
                  ))}
                  {form.photos.length < 6 && (
                    <div onClick={() => fileInputRef.current.click()} style={{ width: 72, height: 72, borderRadius: 10, border: '1px dashed rgba(232,98,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.4rem', color: 'rgba(232,98,42,0.6)' }}>+</div>
                  )}
                </div>
              )}

              {form.photos.length === 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  {/* Take photo */}
                  <div
                    onClick={() => cameraInputRef.current.click()}
                    style={{ background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.5rem 1rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#E8622A'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#F5F0E8', marginBottom: '0.2rem' }}>Take a photo</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)' }}>Use your camera now</div>
                  </div>
                  {/* Upload from gallery */}
                  <div
                    onClick={() => fileInputRef.current.click()}
                    style={{ background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.5rem 1rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#E8622A'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🖼️</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#F5F0E8', marginBottom: '0.2rem' }}>Upload photo</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)' }}>Pick from your gallery</div>
                  </div>
                </div>
              )}

              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.3)', textAlign: 'center' }}>
                You can add up to 6 photos &mdash; more photos = more buyers!
              </div>

              {/* Hidden file inputs */}
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handlePhotoUpload} style={{ display: 'none' }} />
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} style={{ display: 'none' }} />
            </div>

            {/* Item name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>What are you selling?</label>
              <input style={input} type="text" placeholder="e.g. Ladies ankle boots — size 38" value={form.itemName} onChange={e => update('itemName', e.target.value)} />
              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)', marginTop: '0.3rem' }}>Be specific — mention size, colour, brand if you know it</div>
            </div>

            {/* Category */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>What category?</label>
              <select style={{ ...input, cursor: 'pointer' }} value={form.category} onChange={e => update('category', e.target.value)}>
                <option value="">Pick a category</option>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>

            {/* Condition */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ ...label, marginBottom: '0.6rem' }}>What condition is it in?</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {CONDITIONS.map(cond => (
                  <div
                    key={cond.id}
                    onClick={() => update('condition', cond.id)}
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: 12,
                      border: `0.5px solid ${form.condition === cond.id ? '#E8622A' : 'rgba(255,255,255,0.07)'}`,
                      background: form.condition === cond.id ? 'rgba(232,98,42,0.1)' : '#1E1E1E',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>{cond.icon}</span>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: form.condition === cond.id ? '#E8622A' : '#F5F0E8' }}>{cond.label}</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)' }}>{cond.sublabel}</div>
                    </div>
                    {form.condition === cond.id && <div style={{ marginLeft: 'auto', width: 20, height: 20, borderRadius: '50%', background: '#E8622A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#fff', flexShrink: 0 }}>&#10003;</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={label}>Your price</label>
              <div style={{ display: 'flex', background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '0.85rem 1rem', background: 'rgba(196,154,42,0.1)', color: '#C49A2A', fontSize: '0.95rem', fontWeight: 700, borderRight: '0.5px solid rgba(255,255,255,0.07)', fontFamily: 'Syne, sans-serif' }}>K</div>
                <input style={{ ...input, border: 'none', borderRadius: 0 }} type="number" placeholder="0" value={form.price} onChange={e => update('price', e.target.value)} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)', marginTop: '0.3rem' }}>
                Set a fair price — buyers can message you on WhatsApp to negotiate
              </div>
            </div>

            <button onClick={nextStep} disabled={!form.itemName || !form.category || !form.condition || !form.price} style={{ ...bigBtn, background: form.itemName && form.category && form.condition && form.price ? '#E8622A' : '#2A2A2A', color: form.itemName && form.category && form.condition && form.price ? '#fff' : 'rgba(245,240,232,0.3)' }}>
              Continue &rarr;
            </button>
            <button onClick={prevStep} style={backBtn}>&larr; Back</button>
          </div>
        )}

        {/* ========== STEP 4: REVIEW & AGREE ========== */}
        {step === 4 && (
          <div style={card}>
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.4rem' }}>
                Almost done!
              </h2>
              <p style={{ fontSize: '0.88rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6 }}>
                Just check your details below and agree to our simple rules.
              </p>
            </div>

            {/* Summary */}
            <div style={{ background: '#1E1E1E', borderRadius: 12, padding: '1rem', marginBottom: '1.25rem' }}>
              {[
                ['Your name', `${form.firstName} ${form.lastName}`],
                ['WhatsApp', `+260 ${form.phone}`],
                ['Location', form.location],
                ['Shop name', form.shopName],
                ['First item', form.itemName],
                ['Price', `K${form.price}`],
              ].map(([k, v]) => v && (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)', fontSize: '0.82rem' }}>
                  <span style={{ color: 'rgba(245,240,232,0.5)' }}>{k}</span>
                  <span style={{ color: '#F5F0E8', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Commission info */}
            <div style={{ background: 'rgba(196,154,42,0.07)', border: '0.5px solid rgba(196,154,42,0.2)', borderRadius: 12, padding: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#C49A2A', marginBottom: '0.5rem' }}>How commission works</div>
              <div style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                Listing is FREE. We only take a small % when you make a sale. If you don't sell, you don't pay anything.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[['3%', 'Under K500'], ['5%', 'K500-K2,000'], ['7%', 'Above K2,000']].map(([pct, range]) => (
                  <div key={pct} style={{ flex: 1, background: '#1E1E1E', borderRadius: 8, padding: '0.5rem', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#E8622A' }}>{pct}</div>
                    <div style={{ fontSize: '0.62rem', color: 'rgba(245,240,232,0.4)', marginTop: 2 }}>{range}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Agree */}
            <div onClick={() => update('agreeTerms', !form.agreeTerms)} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: '1.5rem' }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, border: `0.5px solid ${form.agreeTerms ? '#E8622A' : 'rgba(255,255,255,0.2)'}`, background: form.agreeTerms ? '#E8622A' : '#1E1E1E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all 0.2s' }}>
                {form.agreeTerms && <span style={{ color: '#fff', fontSize: '0.7rem' }}>&#10003;</span>}
              </div>
              <span style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.55)', lineHeight: 1.6 }}>
                I agree to sell honestly, describe my items accurately, and respond to buyers within a reasonable time. I understand FAMUZA Market can remove listings that are misleading.
              </span>
            </div>

            <button onClick={handleSubmit} disabled={!form.agreeTerms} style={{ ...bigBtn, background: form.agreeTerms ? '#25D366' : '#2A2A2A', color: form.agreeTerms ? '#fff' : 'rgba(245,240,232,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
              <WaIcon size={18} /> Submit my application
            </button>
            <button onClick={prevStep} style={backBtn}>&larr; Back</button>
          </div>
        )}

        {/* ========== STEP 5: SUCCESS ========== */}
        {step === TOTAL_STEPS && (
          <div style={{ ...card, textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.6rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.5rem' }}>
              You're almost live!
            </h2>
            <p style={{ fontSize: '0.88rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.75, marginBottom: '1.75rem' }}>
              Your application has been sent to FAMUZA. We will review it and contact you on WhatsApp <strong style={{ color: '#F5F0E8' }}>within 24 hours</strong>. Existing UNIFAM customers are usually approved within a few hours!
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '2rem', textAlign: 'left' }}>
              {[
                ['✅', 'Account created', true],
                ['✅', 'WhatsApp number saved', true],
                ['⏳', 'Waiting for FAMUZA approval', false],
                ['📦', 'After approval — your listing goes live!', false],
              ].map(([icon, text, done]) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#1E1E1E', borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.85rem', color: done ? '#F5F0E8' : 'rgba(245,240,232,0.5)' }}>
                  <span style={{ fontSize: '1rem' }}>{icon}</span>
                  {text}
                </div>
              ))}
            </div>

            <button
              onClick={() => window.open('https://wa.me/260970000000?text=Hi! I just applied to sell on FAMUZA Market. My shop name is ' + form.shopName, '_blank')}
              style={{ ...bigBtn, background: '#25D366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}
            >
              <WaIcon size={18} /> Join FAMUZA sellers WhatsApp group
            </button>

            <Link to="/">
              <button style={{ ...bigBtn, background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.6)', marginTop: '0.6rem' }}>
                Browse marketplace while you wait
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
